import type { OrganizationScope } from "@/types/organization-scope"
import { isOrganizationScopeSwitchable } from "@/utils/organizationScopeSwitch"

export type BootstrapScopeSource =
  | "server-token"
  | "artifact-handoff"
  | "observed-token"
  | "legacy-handoff"
  | "preference"
  | "default"
  | "none"

export interface BootstrapScopeResolution {
  scopeId: string
  source: BootstrapScopeSource
}

export interface BootstrapScopeCandidates {
  organizations: OrganizationScope[]
  serverScopeId?: string
  handoffScopeId?: string
  observedScopeId?: string
  legacyHandoffScopeId?: string
  preferredScopeId?: string
}

function isAvailableScope(organizations: OrganizationScope[], scopeId?: string) {
  return Boolean(scopeId && organizations.some(
    organization =>
      organization.id === scopeId
      && isOrganizationScopeSwitchable(organization),
  ))
}

function resolveDefaultScope(organizations: OrganizationScope[]) {
  const switchable = organizations.filter(isOrganizationScopeSwitchable)
  return switchable.find(scope => scope.type === "chain-headquarter")
    ?? switchable.find(scope => scope.type === "chain-region")
    ?? switchable.find(scope => scope.type === "shop" || scope.type === "chain-store")
    ?? switchable[0]
}

/**
 * Resolves the only runtime scope candidate used during bootstrap. Persistent
 * preferences are deliberately last because they describe a future choice,
 * not the organization already encoded in the current access token.
 */
export function resolveBootstrapScope(
  candidates: BootstrapScopeCandidates,
): BootstrapScopeResolution {
  const ordered: Array<[BootstrapScopeSource, string | undefined]> = [
    ["server-token", candidates.serverScopeId],
    ["artifact-handoff", candidates.handoffScopeId],
    ["observed-token", candidates.observedScopeId],
    ["legacy-handoff", candidates.legacyHandoffScopeId],
    ["preference", candidates.preferredScopeId],
  ]
  for (const [source, scopeId] of ordered) {
    if (isAvailableScope(candidates.organizations, scopeId)) {
      return { scopeId: scopeId!, source }
    }
  }
  const fallback = resolveDefaultScope(candidates.organizations)
  return fallback
    ? { scopeId: fallback.id, source: "default" }
    : { scopeId: "", source: "none" }
}
